import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, ShieldCheck, Leaf, Shield, ClipboardCheck, Briefcase, Users, Headphones, MapPin, CheckCircle } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { companyInfo, services, whyChooseUs, testimonials } from '../data/mock';
import AnimatedSection from '../components/AnimatedSection';
import Marquee from '../components/Marquee';

const Home = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const servicesPath = language === 'en' ? '/services' : '/servizi';
  const quotePath = language === 'en' ? '/quote' : '/preventivo';
  const contactPath = language === 'en' ? '/contact' : '/contatti';
  
  const [titleLoaded, setTitleLoaded] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, 100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 1.05]);

  useEffect(() => {
    const timer = setTimeout(() => setTitleLoaded(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const titleVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  const lineVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      clipPath: 'inset(0 100% 0 0)'
    },
    visible: { 
      opacity: 1, 
      y: 0,
      clipPath: 'inset(0 0% 0 0)',
      transition: {
        duration: 0.8,
        ease: [0.21, 0.47, 0.32, 0.98]
      }
    }
  };

  const marqueeItems = language === 'en' 
    ? ['ISO 9001 Quality Management', 'ISO 14001 Environmental', 'ISO 45001 Health & Safety', 'ISO 27001 Information Security', 'HSE Compliance', 'Trusted by Fortune 500', 'London • Milan • Rome']
    : ['ISO 9001 Gestione Qualità', 'ISO 14001 Ambientale', 'ISO 45001 Salute e Sicurezza', 'ISO 27001 Sicurezza Informazioni', 'Conformità HSE', 'Fiducia delle Fortune 500', 'London • Milan • Rome'];

  return (
    <div className="home-page">
      {/* Kinetic Hero Section */}
      <motion.section 
        className="hero-section-kinetic"
        style={{ y: heroY, opacity: heroOpacity }}
      >
        <motion.div 
          className="hero-background-parallax"
          style={{ scale: heroScale }}
        >
          <div className="hero-overlay"></div>
          <img 
            src="https://images.unsplash.com/photo-1582653291997-079a1c04e5a1?w=2400&h=1200&fit=crop&q=90" 
            alt="Luxury consulting boardroom"
            className="hero-bg-image"
          />
        </motion.div>
        
        <div className="hero-content-kinetic">
          <div className="hero-left-kinetic">
            <motion.div
              initial="hidden"
              animate={titleLoaded ? "visible" : "hidden"}
              variants={titleVariants}
              className="hero-title-kinetic"
            >
              <motion.div variants={lineVariants} className="title-line">
                {language === 'en' ? 'ISO Certification &' : 'Certificazioni ISO e'}
              </motion.div>
              <motion.div variants={lineVariants} className="title-line">
                {language === 'en' ? 'HSE Compliance' : 'Conformità HSE'}
              </motion.div>
            </motion.div>
            
            <motion.p 
              className="hero-subtitle-kinetic"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              {t.home.heroSubtitle}
            </motion.p>
            
            <motion.div 
              className="hero-buttons-kinetic"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
            >
              <Link to={quotePath} className="btn-primary-kinetic">
                <span>{t.home.requestQuote}</span>
                <ArrowRight size={20} />
              </Link>
              <Link to={servicesPath} className="btn-secondary-kinetic">
                <span>{t.home.discoverServices}</span>
                <ArrowRight size={20} />
              </Link>
            </motion.div>
            
            <motion.div 
              className="hero-stats-kinetic"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              <div className="stat-item-kinetic">
                <motion.div 
                  className="stat-number-kinetic"
                  initial={{ scale: 0.5 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.4, duration: 0.6, type: "spring" }}
                >
                  100+
                </motion.div>
                <div className="stat-label-kinetic">{t.home.stats.companies}</div>
              </div>
              <div className="stat-divider-kinetic"></div>
              <div className="stat-item-kinetic">
                <motion.div 
                  className="stat-number-kinetic"
                  initial={{ scale: 0.5 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.5, duration: 0.6, type: "spring" }}
                >
                  15+
                </motion.div>
                <div className="stat-label-kinetic">{t.home.stats.years}</div>
              </div>
              <div className="stat-divider-kinetic"></div>
              <div className="stat-item-kinetic">
                <motion.div 
                  className="stat-number-kinetic"
                  initial={{ scale: 0.5 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.6, duration: 0.6, type: "spring" }}
                >
                  98%
                </motion.div>
                <div className="stat-label-kinetic">{t.home.stats.success}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Editorial Marquee */}
      <div className="editorial-marquee-section">
        <Marquee items={marqueeItems} speed={40} />
      </div>

      {/* Services Section - Animated */}
      <section className="services-section-premium">
        <AnimatedSection className="section-header">
          <h2 className="section-title">{t.home.servicesTitle}</h2>
          <p className="section-description">
            {t.home.servicesSubtitle}
          </p>
        </AnimatedSection>
        <div className="services-grid-premium">
          {services.map((service, idx) => (
            <AnimatedSection 
              key={service.id} 
              delay={idx * 0.1}
              className="service-card-premium"
            >
              <motion.div 
                className="service-icon-premium"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {service.icon === 'award' && <Award size={32} />}
                {service.icon === 'shield-check' && <ShieldCheck size={32} />}
                {service.icon === 'leaf' && <Leaf size={32} />}
                {service.icon === 'shield' && <Shield size={32} />}
                {service.icon === 'clipboard-check' && <ClipboardCheck size={32} />}
              </motion.div>
              <h3 className="service-title-premium">{service.title}</h3>
              <p className="service-subtitle-premium">
                {language === 'en' ? service.subtitleEn : service.subtitle}
              </p>
              <p className="service-description-premium">
                {language === 'en' ? service.descriptionEn : service.description}
              </p>
              <ul className="service-benefits-premium">
                {(language === 'en' ? service.benefitsEn : service.benefits).slice(0, 3).map((benefit, idx) => (
                  <li key={idx}>
                    <CheckCircle size={16} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <motion.div
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Link to={servicesPath} className="service-link-premium">
                  {t.common.learnMore} <ArrowRight size={16} />
                </Link>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Numbered Manifesto - Why Choose Us */}
      <section className="manifesto-section">
        <AnimatedSection className="section-header">
          <h2 className="section-title-manifesto">{t.home.whyTitle}</h2>
          <p className="section-description">
            {t.home.whySubtitle}
          </p>
        </AnimatedSection>
        
        <AnimatedSection className="manifesto-image-spotlight" delay={0.2}>
          <div className="spotlight-frame">
            <img 
              src="https://images.unsplash.com/photo-1610374792793-f016b77ca51a?w=1600&h=700&fit=crop&q=90" 
              alt="Luxury office with city skyline"
              className="spotlight-image"
            />
          </div>
        </AnimatedSection>
        
        <div className="manifesto-grid">
          {whyChooseUs.map((item, idx) => (
            <AnimatedSection 
              key={idx} 
              delay={idx * 0.15}
              className="manifesto-card"
            >
              <motion.div 
                className="manifesto-number"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 + 0.2, type: "spring" }}
              >
                {String(idx + 1).padStart(2, '0')}
              </motion.div>
              <motion.div 
                className="manifesto-icon"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                {item.icon === 'briefcase' && <Briefcase size={28} />}
                {item.icon === 'users' && <Users size={28} />}
                {item.icon === 'headphones' && <Headphones size={28} />}
                {item.icon === 'map-pin' && <MapPin size={28} />}
              </motion.div>
              <h3 className="manifesto-title">{language === 'en' ? item.titleEn : item.title}</h3>
              <p className="manifesto-description">{language === 'en' ? item.descriptionEn : item.description}</p>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Coverage Section */}
      <section className="coverage-section-premium">
        <AnimatedSection className="coverage-content-premium">
          <h2 className="coverage-title-premium">{t.home.coverageTitle}</h2>
          <p className="coverage-description-premium">
            {t.home.coverageSubtitle}
          </p>
          <motion.div 
            className="coverage-regions-premium"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.1
                }
              }
            }}
          >
            {companyInfo.regions.map((region, idx) => (
              <motion.div 
                key={idx} 
                className="region-tag-premium"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 8px 24px rgba(212, 175, 55, 0.3)"
                }}
              >
                <MapPin size={18} />
                {region}
              </motion.div>
            ))}
          </motion.div>
        </AnimatedSection>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section-premium">
        <AnimatedSection className="section-header">
          <h2 className="section-title">{t.home.testimonialsTitle}</h2>
          <p className="section-description">
            {t.home.testimonialsSubtitle}
          </p>
        </AnimatedSection>
        
        <AnimatedSection className="testimonial-image-spotlight" delay={0.2}>
          <div className="spotlight-frame">
            <img 
              src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=1600&h=700&fit=crop&q=90" 
              alt="Executive business meeting"
              className="spotlight-image"
            />
          </div>
        </AnimatedSection>
        
        <div className="testimonials-grid-premium">
          {testimonials.map((testimonial, idx) => (
            <AnimatedSection 
              key={testimonial.id}
              delay={idx * 0.15}
              className="testimonial-card-premium"
            >
              <div className="testimonial-stars-premium">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <motion.span 
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.15 + i * 0.1 }}
                  >
                    ★
                  </motion.span>
                ))}
              </div>
              <p className="testimonial-text-premium">"{language === 'en' ? testimonial.textEn : testimonial.text}"</p>
              <div className="testimonial-author-premium">
                <div className="author-name-premium">{testimonial.name}</div>
                <div className="author-company-premium">{testimonial.company}</div>
                <div className="author-location-premium">{testimonial.location}</div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* CTA Section - Premium */}
      <section className="cta-section-premium">
        <AnimatedSection className="cta-content-premium">
          <motion.h2 
            className="cta-title-premium"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {t.home.ctaTitle}
          </motion.h2>
          <motion.p 
            className="cta-description-premium"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            {t.home.ctaSubtitle}
          </motion.p>
          <motion.div 
            className="cta-buttons-premium"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link to={quotePath} className="btn-primary-large">
                {t.home.ctaButton}
                <ArrowRight size={20} />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link to={contactPath} className="btn-secondary-large">
                {t.home.ctaContact}
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </AnimatedSection>
      </section>
    </div>
  );
};

export default Home;
